import React, { useState, useRef } from 'react';
import { UploadCloud, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import adminApi from '../lib/adminApi';

const CHUNK_SIZE = 5 * 1024 * 1024;
const CATEGORIES = [
  { key: 'mariage', label: 'Mariage' },
  { key: 'festival', label: 'Festival' },
  { key: 'corporate', label: 'Entreprise' },
  { key: 'club', label: 'Club' },
  { key: 'autre', label: 'Autre / Produit' },
];

const cleanTitle = (name) =>
  name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();

const uploadPhoto = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await adminApi.post('/api/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return res.data.url;
};

const uploadVideo = async (file, onProgress) => {
  const uploadId = `v${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  for (let i = 0; i < totalChunks; i += 1) {
    const fd = new FormData();
    fd.append('upload_id', uploadId);
    fd.append('chunk_index', String(i));
    fd.append('file', file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE), `${i}.part`);
    // eslint-disable-next-line no-await-in-loop
    await adminApi.post('/api/admin/upload/video/chunk', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    onProgress(Math.round(((i + 1) / totalChunks) * 100));
  }
  const res = await adminApi.post('/api/admin/upload/video/complete', {
    upload_id: uploadId,
    filename: file.name,
    total_chunks: totalChunks,
  });
  return res.data.url;
};

export const BulkMediaUpload = ({ onDone }) => {
  const [category, setCategory] = useState('mariage');
  const [publishNow, setPublishNow] = useState(true);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const setRow = (i, patch) => setRows((prev) => prev.map((r, k) => (k === i ? { ...r, ...patch } : r)));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setRows(files.map((f) => ({ name: f.name, status: 'wait', progress: 0 })));
    setBusy(true);

    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      const isVideo = file.type.startsWith('video/') || /\.(mp4|mov|webm|m4v)$/i.test(file.name);
      setRow(i, { status: 'up' });
      try {
        const url = isVideo
          // eslint-disable-next-line no-await-in-loop
          ? await uploadVideo(file, (p) => setRow(i, { progress: p }))
          // eslint-disable-next-line no-await-in-loop
          : await uploadPhoto(file);
        const title = cleanTitle(file.name);
        // eslint-disable-next-line no-await-in-loop
        await adminApi.post('/api/admin/media', {
          type: isVideo ? 'video' : 'photo',
          source: 'upload',
          url,
          category,
          title,
          titleEn: title,
          titleAr: title,
          titleIt: title,
          published: publishNow,
          sortOrder: 100 + i,
        });
        setRow(i, { status: 'ok', progress: 100 });
      } catch (err) {
        setRow(i, { status: 'ko', error: err.response?.data?.detail || 'Échec de l\'envoi' });
      }
    }

    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
    onDone?.();
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-8" data-testid="bulk-upload">
      <h2 className="text-white font-semibold">Ajout rapide — photos & vidéos</h2>
      <p className="text-gray-400 text-sm mt-1 mb-5">
        Sélectionnez plusieurs fichiers d'un coup : ils s'ajoutent à la galerie en plus de ceux déjà présents.
        Photos JPG/PNG/HEIC jusqu'à 25 Mo, vidéos MP4/MOV/WebM jusqu'à 300 Mo.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        <div>
          <Label className="text-gray-300">Catégorie appliquée</Label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full mt-1 bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
            data-testid="bulk-category"
          >
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <label className="flex items-end gap-2 text-gray-300 text-sm pb-2">
          <input
            type="checkbox"
            checked={publishNow}
            onChange={(e) => setPublishNow(e.target.checked)}
            data-testid="bulk-publish"
          />
          Publier tout de suite sur le site
        </label>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/mp4,video/webm,video/quicktime,.heic,.heif,.mov"
        onChange={handleFiles}
        className="hidden"
      />
      <Button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="bg-orange-600 hover:bg-orange-500"
        data-testid="bulk-choose-files"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
        {busy ? 'Envoi en cours...' : 'Choisir des fichiers'}
      </Button>

      {rows.length > 0 && (
        <ul className="mt-5 space-y-2" data-testid="bulk-progress">
          {rows.map((r, i) => (
            <li key={`${r.name}-${i}`} className="flex items-center gap-3 text-sm">
              {r.status === 'ok' && <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />}
              {r.status === 'ko' && <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
              {r.status === 'up' && <Loader2 className="w-4 h-4 text-orange-400 animate-spin shrink-0" />}
              {r.status === 'wait' && <span className="w-4 h-4 rounded-full border border-gray-700 shrink-0" />}
              <span className="text-gray-300 truncate max-w-[280px]">{r.name}</span>
              {r.status === 'up' && r.progress > 0 && <span className="text-orange-400">{r.progress}%</span>}
              {r.status === 'ko' && <span className="text-red-400 text-xs">{r.error}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default BulkMediaUpload;
